import { cn } from '@/lib/utils'
import { getPlayerIcon } from '@/config/playerIcons'

export type Role = 'Biologist' | 'Historian' | 'Diver' | 'Hunter' | 'Fixer' | string

interface RoleCardProps {
  role: Role
  description?: string
  className?: string
}

const roleConfig: Record<string, { color: string; bgColor: string }> = {
  Biologist: {
    color: 'text-[#367A25]',
    bgColor: 'bg-[#367A25]/10 border-[#367A25]/30',
  },
  Historian: {
    color: 'text-[#DD6832]',
    bgColor: 'bg-[#DD6832]/10 border-[#DD6832]/30',
  },
  Diver: {
    color: 'text-[#12356B]',
    bgColor: 'bg-[#12356B]/10 border-[#12356B]/30',
  },
  Hunter: {
    color: 'text-[#DD6832]',
    bgColor: 'bg-[#DD6832]/10 border-[#DD6832]/30',
  },
  Fixer: {
    color: 'text-[#12356B]',
    bgColor: 'bg-[#E2E5EC] border-[#CED4DE]',
  },
}

export function getRoleConfig(role: string) {
  return roleConfig[role] || { color: 'text-slate-700', bgColor: 'bg-[#E2E5EC] border-[#CED4DE]' }
}

export function RoleCard({ role, description, className }: RoleCardProps) {
  const config = getRoleConfig(role)
  const iconUrl = getPlayerIcon(role)

  return (
    <div className={cn('rounded-lg border p-4', config.bgColor, className)}>
      <div className="flex items-center gap-3">
        <div className={cn(
          'size-12 rounded-full flex items-center justify-center',
          'bg-background shadow-sm'
        )}>
          {iconUrl ? (
            <img src={iconUrl} alt={`${role} icon`} className="size-10 object-contain" />
          ) : (
            <span className={cn('text-xl font-bold', config.color)}>
              {role.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={cn('font-semibold text-lg', config.color)}>{role}</h3>
          {description && (
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          )}
        </div>
      </div>
    </div>
  )
}

interface RoleAvatarProps {
  role: Role
  name: string
  isCurrentPlayer?: boolean
  className?: string
}

export function RoleAvatar({ role, name, isCurrentPlayer = false, className }: RoleAvatarProps) {
  const config = getRoleConfig(role)
  const initial = name.charAt(0).toUpperCase()
  const iconUrl = getPlayerIcon(role)

  return (
    <div className={cn(
      'flex items-center gap-4 p-4 rounded-lg border transition-colors',
      isCurrentPlayer ? 'bg-[#E2E5EC] border-[#12356B]/40' : 'bg-white hover:bg-[#E2E5EC]/40 border-[#CED4DE]',
      className
    )}>
      <div className={cn(
        'size-12 rounded-full flex items-center justify-center text-xl font-medium',
        config.bgColor, config.color
      )}>
        {iconUrl ? (
          <img src={iconUrl} alt={`${role} icon`} className="size-10 object-contain" />
        ) : (
          initial
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-lg truncate">{name}</span>
          {isCurrentPlayer && (
            <span className="text-xs uppercase tracking-wider text-[#DD6832] font-semibold bg-[#DD6832]/10 px-2 py-1 rounded">
              You
            </span>
          )}
        </div>
        <span className={cn('text-base', config.color)}>{role || 'Unassigned'}</span>
      </div>
    </div>
  )
}
